import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { popsApi } from "../lib/api";
import { Printer } from "lucide-react";

const money = (v, cur = "USD") =>
  `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

export default function InvoicePrint() {
  const { invoiceId } = useParams();
  const [inv, setInv] = useState(null);

  useEffect(() => {
    popsApi.invoice(invoiceId).then(setInv).catch(console.error);
  }, [invoiceId]);

  if (!inv) return <div className="p-10 text-sm text-gray-500">Loading invoice…</div>;
  const cur = inv.fx.currency;
  const fx = inv.fx.rate;

  return (
    <div className="min-h-screen bg-white text-[#111] p-10 max-w-4xl mx-auto" data-testid="invoice-print-page">
      <button
        onClick={() => window.print()}
        data-testid="print-button"
        className="print:hidden fixed top-4 right-4 flex items-center gap-1.5 px-4 py-2 bg-[#00579e] text-white rounded text-sm"
      >
        <Printer className="w-4 h-4" /> Print / Save as PDF
      </button>

      {/* Head */}
      <div className="flex justify-between items-start border-b-2 border-[#0B2545] pb-6">
        <div>
          <div className="text-2xl font-bold text-[#0B2545]">Acme Managed Cloud</div>
          <div className="text-xs text-gray-500 mt-1">Managed Cloud Services · L2 Partner of CloudScore Distribution</div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold">{inv.doc_type === "credit_note" ? "CREDIT NOTE" : "INVOICE"}</div>
          <div className="font-mono text-sm">{inv.number}</div>
          <div className="text-xs text-gray-500">Period: {inv.period}</div>
          <div className="text-xs text-gray-500">State: {inv.state.toUpperCase()}{inv.issued_at ? ` · issued ${inv.issued_at.slice(0, 10)}` : ""}</div>
        </div>
      </div>

      {/* Parties */}
      <div className="grid grid-cols-2 gap-8 py-6 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Bill to</div>
          <div className="font-semibold">{inv.customer.legal_entity}</div>
          <div className="text-xs text-gray-600">Tax ID: {inv.customer.tax_id} · PO: {inv.customer.po_number}</div>
          <div className="text-xs text-gray-600">Terms: {inv.customer.payment_terms}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Currency & FX</div>
          <div className="text-xs text-gray-600">1 USD = {fx} {cur}</div>
          <div className="text-xs text-gray-600">{inv.fx.source}</div>
          <div className="text-xs text-gray-600">locked {inv.fx.locked_at?.slice(0, 16).replace("T", " ")} UTC</div>
        </div>
      </div>

      {/* Lines */}
      <table className="w-full text-sm border-t border-gray-200">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-gray-400">
            <th className="py-2">Description</th>
            <th className="py-2 text-right">Qty</th>
            <th className="py-2 text-right">Unit rate (USD)</th>
            <th className="py-2 text-right">Amount ({cur})</th>
          </tr>
        </thead>
        <tbody>
          {inv.lines.map((l, i) => (
            <tr key={i} className="border-t border-gray-100">
              <td className="py-2">
                {l.description}
                {l.formula && <div className="text-[10px] text-gray-400 font-mono">{l.formula}</div>}
              </td>
              <td className="py-2 text-right font-mono">{Number(l.qty).toLocaleString()}</td>
              <td className="py-2 text-right font-mono">{Number(l.unit_rate).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
              <td className={`py-2 text-right font-mono ${l.amount < 0 ? "text-red-600" : ""}`}>{money(l.amount * fx, "")}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[#0B2545]">
            <td colSpan={3} className="py-3 text-right font-semibold">TOTAL DUE</td>
            <td className="py-3 text-right font-mono font-bold text-lg" data-testid="print-total">{money(inv.totals.billed_amount, cur)}</td>
          </tr>
        </tfoot>
      </table>

      <div className="mt-8 text-[10px] text-gray-400 border-t border-gray-100 pt-3">
        Every line is traceable to the FOCUS-normalized ledger (config {inv.config_version}). The attached cost report
        reconciles to this invoice to the cent. Corrections after issue are made exclusively via credit note.
      </div>
    </div>
  );
}
